import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminUser } from '@/types';
import { authApi } from '@/api/auth';
import { supabase } from '@/lib/supabase';

interface AuthState {
  user: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  /** Password step — returns login_attempt; no session until completeLoginWithTotp. */
  beginLogin: (email: string, password: string) => Promise<{ login_attempt: string }>;
  completeLoginWithTotp: (loginAttempt: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AdminUser | null) => void;
  initFromSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: false,
      isAuthenticated: false,

      beginLogin: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const result = await authApi.beginLogin(email, password);
          set({ isLoading: false });
          return { login_attempt: result.login_attempt };
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      completeLoginWithTotp: async (loginAttempt: string, code: string) => {
        set({ isLoading: true });
        try {
          const user = await authApi.completeLoginWithTotp(loginAttempt, code);
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        await authApi.logout();
        set({ user: null, isAuthenticated: false });
      },

      setUser: (user: AdminUser | null) => {
        set({ user, isAuthenticated: !!user });
      },

      initFromSession: async () => {
        set({ isLoading: true });
        try {
          const user = await authApi.me();
          set({ user, isAuthenticated: !!user, isLoading: false });
        } catch {
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },
    }),
    {
      name: 'otomator-admin-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

export function initSupabaseAuthListener() {
  const store = useAuthStore.getState();
  store.initFromSession();

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT' || !session) {
      useAuthStore.setState({ user: null, isAuthenticated: false });
    } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      useAuthStore.getState().initFromSession();
    }
  });
}
